import { graphql } from 'msw';
import { MOCK_CLIENT_ID } from '..';
import { hasQueryParam } from '../../../testing/helpers';
import {
  FeatureFlagsResponse,
  getFeatureFlagsResponseMock,
  getSavedItemsBrowserSuccessMock,
  getSavedItemsEmptyMock,
  getSavedItemsLimitedStockMock,
  getSavedItemsOutfitsBrowserSuccessMock,
  getSavedItemsOutfitsMswMock,
  getSavedItemsOutOfStockMock,
  getSavedItemsPageTwoMock,
  getSavedItemsPageZeroMock,
  getSavedItemsSuccessMock,
} from '../../components/SavedItemsLayout/mockData';
import { GetClientCapabilitiesAndBusinessLineQuery } from '../../utils/hooks/getClientCapabilitiesAndBusinessLine';
import type {
  GetSavedItemsQuery,
  GetSavedItemsQueryVariables,
} from '../../utils/hooks/getSavedItems';
import type {
  GetSavedItemsOutfitsQuery,
  GetSavedItemsOutfitsQueryVariables,
} from '../../utils/hooks/getSavedItemsOutfits';
import {
  GetAutoShipChurnPreventionOfferQuery,
  GetAutoShipChurnPreventionOfferQueryVariables,
} from '../../components/FixCancellation/ChurnPrevention/GetAutoShipChurnPreventionOffer';
import { getAutoShipChurnPreventionOfferMock } from '../../components/FixCancellation/ChurnPrevention/mockData';
import { GetClientConfigParamsQuery } from '../../utils/hooks/getClientConfigParams';
import { getClientConfigsEnabledResponseData } from '../../components/FeedbackModal/mockData';
import {
  mockLookbookHandler,
  defaultLookbook,
} from '../../components/LookbookGallery/graphql/mockData';
import {
  cloudinaryUpload,
  getClientInfo,
  getClientSelfie,
  getClientFullBody,
  clientSelfieUpdate,
  clientPhotoFullBodyCreate,
  visionOnboardingComplete,
} from '../../components/LookbookGallery/Onboarding/graphql/mockData';

const stitchfix = graphql.link(
  process.env.NEXT_PUBLIC_GRAPHQL_API_ENDPOINT || '',
);

const getClientCapabilitiesAndBusinessLine =
  stitchfix.query<GetClientCapabilitiesAndBusinessLineQuery>(
    'getClientCapabilitiesAndBusinessLine',
    (_req, res, ctx) =>
      res(
        ctx.data({
          client: {
            __typename: 'Client' as const,
            id: MOCK_CLIENT_ID || '00000000',
            businessLine: 'WOMENS',
            firstName: 'Buster',
            isAuthenticatedClient: true,
            capabilities: {
              canShop: true,
              __typename: 'ClientCapabilities' as const,
            },
          },
        }),
      ),
  );

export const getSavedItems = stitchfix.query<
  GetSavedItemsQuery,
  GetSavedItemsQueryVariables
>('getSavedItems', (_req, res, ctx) => {
  const { data } = getSavedItemsSuccessMock;

  return res(ctx.data(data));
});

export const getSavedItemsOutfits = stitchfix.query<
  GetSavedItemsOutfitsQuery,
  GetSavedItemsOutfitsQueryVariables
>('getSavedItemsOutfits', (_req, res, ctx) => {
  const { data } = getSavedItemsOutfitsMswMock;

  return res(ctx.data(data));
});

export const getEmptySavedItems = stitchfix.query<
  GetSavedItemsQuery,
  GetSavedItemsQueryVariables
>('getSavedItems', (_req, res, ctx) => {
  const { data } = getSavedItemsEmptyMock;

  return res(ctx.data(data));
});

export const getSavedItemsPageZero = stitchfix.query<
  GetSavedItemsQuery,
  GetSavedItemsQueryVariables
>('getSavedItems', (_req, res, ctx) => {
  const { data } = getSavedItemsPageZeroMock;

  return res(ctx.data(data));
});

export const getSavedItemsOutOfStock = stitchfix.query<
  GetSavedItemsQuery,
  GetSavedItemsQueryVariables
>('getSavedItems', (_req, res, ctx) => {
  const { data } = getSavedItemsOutOfStockMock;

  return res(ctx.data(data));
});

export const getSavedItemsPageTwo = stitchfix.query<
  GetSavedItemsQuery,
  GetSavedItemsQueryVariables
>('getSavedItems', (_req, res, ctx) => {
  const { data } = getSavedItemsPageTwoMock;

  return res(ctx.data(data));
});

export const getSavedItemsLimitedStock = stitchfix.query<
  GetSavedItemsQuery,
  GetSavedItemsQueryVariables
>('getSavedItems', (_req, res, ctx) => {
  const { data } = getSavedItemsLimitedStockMock;

  return res(ctx.data(data));
});

export const getSavedItemsError = stitchfix.query<
  GetSavedItemsQuery,
  GetSavedItemsQueryVariables
>('getSavedItems', (_req, res, ctx) =>
  res(ctx.errors([{ message: 'Something went wrong' }])),
);

export const getSavedItemsLoading = stitchfix.query<
  GetSavedItemsQuery,
  GetSavedItemsQueryVariables
>('getSavedItems', (_req, res, ctx) =>
  res(
    ctx.status(200),
    ctx.data(getSavedItemsSuccessMock.data),
    ctx.delay(1000),
  ),
);

export const getSavedItemsForBrowserTest = stitchfix.query<
  GetSavedItemsQuery,
  GetSavedItemsQueryVariables
>('getSavedItems', (_req, res, ctx) => {
  const { data: sucessStateData } = getSavedItemsBrowserSuccessMock;

  if (hasQueryParam('showAvailableItems', 'false')) {
    const { data: outOfStockData } = getSavedItemsOutOfStockMock;

    return res(ctx.data(outOfStockData));
  }

  if (hasQueryParam('page', '2')) {
    const { data: pageTwoData } = getSavedItemsPageTwoMock;

    return res(ctx.data(pageTwoData));
  }

  return res(ctx.data(sucessStateData));
});

export const getSavedItemsOutfitsForBrowserTest = stitchfix.query<
  GetSavedItemsOutfitsQuery,
  GetSavedItemsOutfitsQueryVariables
>('getSavedItemsOutfits', (_req, res, ctx) => {
  const { data: successStateData } = getSavedItemsOutfitsBrowserSuccessMock;

  return res(ctx.data(successStateData));
});

export const getAutoShipChurnPreventionOfferModule = stitchfix.query<
  GetAutoShipChurnPreventionOfferQuery,
  GetAutoShipChurnPreventionOfferQueryVariables
>('getAutoShipChurnPreventionOfferModule', (_req, res, ctx) => {
  const { data } = getAutoShipChurnPreventionOfferMock();

  return res(ctx.data(data));
});

export const getFeatureFlags = stitchfix.query<FeatureFlagsResponse>(
  'getFeatureFlags',
  (_req, res, ctx) => {
    return res(ctx.data(getFeatureFlagsResponseMock));
  },
);

export const getNarvarExperimentFlag =
  stitchfix.query<GetClientConfigParamsQuery>(
    'getClientConfigParams',
    (_req, res, ctx) => {
      return res(
        ctx.data(
          getClientConfigsEnabledResponseData as GetClientConfigParamsQuery,
        ),
      );
    },
  );

export const graphHandlers = [
  getClientCapabilitiesAndBusinessLine,
  getSavedItems,
  getAutoShipChurnPreventionOfferModule,
  getFeatureFlags,
  getNarvarExperimentFlag,
];
export const browserGraphHandlers = [
  getClientCapabilitiesAndBusinessLine,
  getSavedItemsForBrowserTest,
  getSavedItemsOutfitsForBrowserTest,
  getAutoShipChurnPreventionOfferModule,
  getFeatureFlags,
  getNarvarExperimentFlag,
  mockLookbookHandler(defaultLookbook),
  cloudinaryUpload,
  getClientInfo,
  getClientSelfie,
  getClientFullBody,
  clientSelfieUpdate,
  clientPhotoFullBodyCreate,
  visionOnboardingComplete,
];
